/**
 * BubblesTable — table view of the same {@link Bubble}s the canvas chart renders.
 *
 * The chart maps each bubble to (x, y, size) coordinates; the table lists every
 * bubble as a row with the metric columns that drive those coordinates plus the
 * roll-up counters. Same click-selection behaviour as the chart: clicking a row
 * opens the same detail panel below.
 *
 * View-mode-aware columns:
 *   - Industry View — one row per industry (rolled up on the client).
 *   - Use Case View — one row per UC in the drilled industry.
 *   - Asset View    — one row per asset (each bubble also identifies industry/UC).
 *   - Account View  — one row per account.
 *
 * Column set is view-specific: the shared "Opps / ARR / Launched ARR" columns
 * always render; identity + influence columns vary by grain.
 */

import { Table, type TableProps } from '@amzn/awsui-components-react';
import type {
  AccountBubble,
  AssetBubble,
  Bubble,
  GhostBubble,
  IndustryBubble,
  UcBubble,
} from './PipelineMap';

interface Props {
  bubbles: Bubble[];
  /** Same view mode the chart uses — drives the column set. */
  viewMode: 'industry' | 'usecase' | 'asset' | 'account';
  /** Currently-selected bubble in the chart (highlights the row). */
  selectedBubble: Bubble | null;
  onSelect: (b: Bubble) => void;
  hasInfluence: boolean;
}

function fmtArr(v: number | undefined | null): string {
  if (v == null || v === 0) return '—';
  if (v >= 1_000_000) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1e3).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

function fmtPct(v: number | undefined | null): string {
  if (v == null) return '—';
  return `${v}%`;
}

function fmtNum(v: number | undefined | null): string {
  if (v == null) return '—';
  return v.toLocaleString('en-US');
}

function fmtDensity(v: number | undefined | null): string {
  if (v == null || v === 0) return '—';
  return v.toFixed(2);
}

/** Stable identity key for a bubble — matches the chart's hit-detection. */
function bubbleKey(b: Bubble): string {
  switch (b.kind) {
    case 'industry':
      return `ind:${b.industry}`;
    case 'uc':
      return `uc:${b.industry}:${b.ucName}`;
    case 'ghost':
      return `ghost:${b.industry}:${b.ucName}`;
    case 'asset':
      return `asset:${b.assetId}`;
    case 'account':
      return `acct:${b.accountName}`;
  }
}

/** Column set for Industry View (one row per industry). */
function industryColumns(): TableProps.ColumnDefinition<Bubble>[] {
  return [
    {
      id: 'industry',
      header: 'Industry',
      cell: (b) => (b as IndustryBubble).industry,
      sortingField: 'industry',
    },
    {
      id: 'opps',
      header: 'Opps',
      cell: (b) => fmtNum(b.oppCount),
    },
    {
      id: 'arr',
      header: 'ARR',
      cell: (b) => fmtArr(b.totalArr),
    },
    {
      id: 'launched-arr',
      header: 'Launched ARR',
      cell: (b) => fmtArr(b.launchedArr),
    },
  ];
}

/** Column set for Use Case View. */
function ucColumns(): TableProps.ColumnDefinition<Bubble>[] {
  return [
    {
      id: 'uc',
      header: 'Use Case',
      cell: (b) => {
        if (b.kind === 'ghost') {
          return (
            <span>
              {(b as GhostBubble).ucName}{' '}
              <span style={{ color: '#f59e0b', fontSize: 12 }}>(New Asset)</span>
            </span>
          );
        }
        return (b as UcBubble).ucName;
      },
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (b) => (b.kind === 'uc' ? (b as UcBubble).priority : '—'),
    },
    {
      id: 'opps',
      header: 'Opps',
      cell: (b) => fmtNum(b.oppCount),
    },
    {
      id: 'arr',
      header: 'ARR',
      cell: (b) => fmtArr(b.totalArr),
    },
    {
      id: 'launched-arr',
      header: 'Launched ARR',
      cell: (b) => fmtArr(b.launchedArr),
    },
    {
      id: 'density',
      header: 'Engagement Depth',
      cell: (b) => (b.kind === 'uc' ? fmtDensity((b as UcBubble).influenceDensityOpp) : '—'),
    },
    {
      id: 'penetration',
      header: '渗透率',
      cell: (b) => (b.kind === 'uc' ? fmtPct((b as UcBubble).penetrationPct) : '—'),
    },
    {
      id: 'ivt',
      header: 'IVT Acts',
      cell: (b) => (b.kind === 'uc' ? fmtNum((b as UcBubble).ivtActs) : '—'),
    },
  ];
}

/** Column set for Asset View. */
function assetColumns(): TableProps.ColumnDefinition<Bubble>[] {
  return [
    {
      id: 'asset',
      header: 'Asset',
      cell: (b) => (b as AssetBubble).assetName,
    },
    {
      id: 'industry',
      header: 'Industry',
      cell: (b) => (b as AssetBubble).industry,
    },
    {
      id: 'uc',
      header: 'Use Case',
      cell: (b) => (b as AssetBubble).ucName ?? '—',
    },
    {
      id: 'opps',
      header: 'Opps',
      cell: (b) => fmtNum(b.oppCount),
    },
    {
      id: 'arr',
      header: 'ARR',
      cell: (b) => fmtArr(b.totalArr),
    },
    {
      id: 'launched-arr',
      header: 'Launched ARR',
      cell: (b) => fmtArr(b.launchedArr),
    },
    {
      id: 'density',
      header: 'Engagement Depth',
      cell: (b) => fmtDensity((b as AssetBubble).influenceDensityOpp),
    },
    {
      id: 'penetration',
      header: '渗透率',
      cell: (b) => fmtPct((b as AssetBubble).penetrationPct),
    },
  ];
}

/** Column set for Account View. */
function accountColumns(): TableProps.ColumnDefinition<Bubble>[] {
  return [
    {
      id: 'account',
      header: 'Account',
      cell: (b) => (b as AccountBubble).accountName,
    },
    {
      id: 'uc-count',
      header: 'UCs',
      cell: (b) => fmtNum((b as AccountBubble).ucCount),
    },
    {
      id: 'opps',
      header: 'Opps',
      cell: (b) => fmtNum(b.oppCount),
    },
    {
      id: 'arr',
      header: 'ARR',
      cell: (b) => fmtArr(b.totalArr),
    },
    {
      id: 'density',
      header: 'Engagement Depth',
      cell: (b) => fmtDensity((b as AccountBubble).influenceDensityOpp),
    },
    {
      id: 'freshness',
      header: 'Freshness',
      cell: (b) => (b as AccountBubble).freshness,
    },
    {
      id: 'best-days',
      header: 'Best Days',
      cell: (b) => fmtNum((b as AccountBubble).bestDaysSince),
    },
  ];
}

export default function BubblesTable({
  bubbles,
  viewMode,
  selectedBubble,
  onSelect,
  hasInfluence: _hasInfluence,
}: Props) {
  const columns =
    viewMode === 'industry'
      ? industryColumns()
      : viewMode === 'asset'
        ? assetColumns()
        : viewMode === 'account'
          ? accountColumns()
          : ucColumns();

  const selectedKey = selectedBubble ? bubbleKey(selectedBubble) : null;

  return (
    <div className="ivt-ipo__table-wrap">
      <Table
        variant="embedded"
        items={bubbles}
        columnDefinitions={columns}
        trackBy={bubbleKey}
        selectionType="single"
        selectedItems={
          selectedKey ? bubbles.filter((b) => bubbleKey(b) === selectedKey) : []
        }
        onSelectionChange={(e) => {
          const first = e.detail.selectedItems[0];
          if (first) onSelect(first);
        }}
        empty={<div style={{ padding: '32px 0', textAlign: 'center', color: '#64748b' }}>没有数据</div>}
      />
    </div>
  );
}
